// Geographic world route map: real equirectangular coastlines + country borders
// (Pacific-centred for an Asia hub), with real port coordinates. Routes draw in on
// scroll-into-view and then "flow" (marching dashes) from the Shanghai hub to the
// core international regions. Pure inline SVG — no tile provider / API key. Map
// geometry + projected points come from worldMap.data.ts (generated from real lng/lat).
//
// Honors prefers-reduced-motion via the shared `reducedMotion` flag.
import { motion } from "framer-motion";

import { reducedMotion } from "../motion/variants";
import {
  WORLD,
  HUB,
  DOMESTIC_PORTS,
  INTL_REGIONS,
  type GeoPoint,
} from "./worldMap.data";

/** Quadratic arc from a→b, bowed toward the top edge for a "great-circle" feel. */
function arc(a: GeoPoint, b: GeoPoint): string {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dist = Math.hypot(a.x - b.x, a.y - b.y);
  const lift = Math.min(150, dist * 0.26 + 24);
  return `M ${a.x} ${a.y} Q ${mx} ${my - lift} ${b.x} ${b.y}`;
}

const ease = [0.16, 1, 0.3, 1] as const;

export interface RouteMapProps {
  hubLabel?: string;
  ariaLabel?: string;
  /** "zh" | "en" — picks the label language for region/hub names. */
  lang?: string;
  className?: string;
}

export default function RouteMap({
  hubLabel,
  ariaLabel,
  lang = "zh",
  className = "",
}: RouteMapProps) {
  const zh = lang !== "en";
  const name = (p: GeoPoint) => (zh ? p.zh : p.en);

  // Label placement: keep text inside the cropped viewBox near the edges.
  const labelFor = (p: GeoPoint) => {
    const anchor = p.x > 800 ? "end" : p.x < 150 ? "start" : "middle";
    const dx = anchor === "end" ? -12 : anchor === "start" ? 12 : 0;
    return { anchor, dx, dy: -14 } as const;
  };

  return (
    <div className={className}>
      <svg
        viewBox={WORLD.viewBox}
        className="h-auto w-full"
        role="img"
        aria-label={ariaLabel ?? "Global shipping-route map centred on Shanghai"}
      >
        <defs>
          <linearGradient id="routeStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--brand-primary)" />
            <stop offset="1" stopColor="var(--brand-azure)" />
          </linearGradient>
          <radialGradient id="ocean" cx="0.5" cy="0.45" r="0.75">
            <stop offset="0" stopColor="#eaf6fc" />
            <stop offset="1" stopColor="#dceef8" />
          </radialGradient>
          <filter id="nodeGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <style>{`
            @keyframes zjl-route-flow { to { stroke-dashoffset: -28; } }
            .zjl-flow { animation: zjl-route-flow 1.1s linear infinite; }
            @media (prefers-reduced-motion: reduce) { .zjl-flow { animation: none; } }
          `}</style>
        </defs>

        {/* Ocean */}
        <path d={WORLD.spherePath} fill="url(#ocean)" />
        {/* Graticule */}
        <path
          d={WORLD.graticulePath}
          fill="none"
          stroke="#cfe6f1"
          strokeWidth="0.6"
          opacity="0.7"
        />
        {/* Land + country borders */}
        <path d={WORLD.landPath} fill="var(--brand-tint)" stroke="none" />
        <path
          d={WORLD.bordersPath}
          fill="none"
          stroke="var(--brand-tint-2)"
          strokeWidth="0.6"
          opacity="0.9"
        />
        {/* Coastline edge for definition */}
        <path d={WORLD.landPath} fill="none" stroke="#a9d4e6" strokeWidth="0.8" />

        {/* Route arcs — draw in once on view, then flow. */}
        <motion.g
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          fill="none"
          strokeLinecap="round"
        >
          {INTL_REGIONS.map((d, i) => {
            const p = arc(HUB, d);
            return (
              <g key={d.id}>
                <motion.path
                  d={p}
                  stroke="url(#routeStroke)"
                  strokeWidth="2"
                  opacity="0.55"
                  variants={{
                    hidden: { pathLength: reducedMotion ? 1 : 0, opacity: reducedMotion ? 0.55 : 0 },
                    visible: {
                      pathLength: 1,
                      opacity: 0.55,
                      transition: {
                        pathLength: { duration: reducedMotion ? 0.001 : 1.3, ease, delay: i * 0.16 },
                        opacity: { duration: 0.2, delay: i * 0.16 },
                      },
                    },
                  }}
                />
                <motion.path
                  d={p}
                  className={reducedMotion ? undefined : "zjl-flow"}
                  stroke="var(--brand-azure)"
                  strokeWidth="2.4"
                  strokeDasharray="2 26"
                  strokeLinecap="round"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 0.95, transition: { duration: 0.3, delay: 0.9 + i * 0.16 } },
                  }}
                />
              </g>
            );
          })}
        </motion.g>

        {/* Domestic ports — small coastal dots (Shanghai is the hub, drawn below). */}
        {DOMESTIC_PORTS.filter((p) => p.id !== HUB.id).map((p, i) => (
          <motion.circle
            key={p.id}
            cx={p.x}
            cy={p.y}
            r="2.6"
            fill="var(--brand-azure)"
            opacity="0.8"
            initial={{ scale: reducedMotion ? 1 : 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.3, delay: 0.2 + i * 0.05, ease }}
          />
        ))}

        {/* International region nodes + labels. */}
        {INTL_REGIONS.map((d, i) => {
          const { anchor, dx, dy } = labelFor(d);
          return (
            <motion.g
              key={d.id}
              initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.2 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.35, delay: 0.7 + i * 0.16, ease }}
            >
              <circle cx={d.x} cy={d.y} r="6.5" fill="var(--brand-azure)" opacity="0.18" />
              <circle
                cx={d.x}
                cy={d.y}
                r="3.6"
                fill="var(--brand-azure)"
                stroke="#fff"
                strokeWidth="1.4"
                filter="url(#nodeGlow)"
              />
              <text
                x={d.x + dx}
                y={d.y + dy}
                textAnchor={anchor}
                fill="var(--brand-azure-text)"
                fontSize="14"
                fontWeight="600"
                className="font-zh"
              >
                {name(d)}
              </text>
            </motion.g>
          );
        })}

        {/* Pulsing hub at Shanghai. */}
        <g>
          {!reducedMotion && (
            <motion.circle
              cx={HUB.x}
              cy={HUB.y}
              r="6"
              fill="var(--brand-primary)"
              initial={{ opacity: 0.5, scale: 1 }}
              animate={{ opacity: [0.5, 0, 0.5], scale: [1, 3, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <circle cx={HUB.x} cy={HUB.y} r="5.5" fill="var(--brand-primary)" stroke="#fff" strokeWidth="2" />
          <text
            x={HUB.x}
            y={HUB.y - 13}
            textAnchor="middle"
            fill="var(--brand-primary)"
            fontSize="15"
            fontWeight="700"
            className="font-zh"
          >
            {hubLabel ?? (zh ? "上海 · 枢纽" : "Shanghai · Hub")}
          </text>
        </g>
      </svg>
    </div>
  );
}
